import { useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import MenuItem from '@mui/material/MenuItem';
import { useWizard } from '../../hooks/WizardProvider';
import StepPanel from '../../components/StepPanel';
import WizardFooter from '../../components/WizardFooter';
import Field from '../../components/ui/Field';
import { GOAL_LABEL_BY_VALUE } from '../../utils/campaignGoals';

const AttributeTypes = [
  { value: 'STRING', label: 'String' },
  { value: 'INT', label: 'Int' },
  { value: 'EMAIL', label: 'Email' },
  { value: 'PASSWORD', label: 'Password' },
  { value: 'PHONE', label: 'Phone' },
  { value: 'RADIO', label: 'Radio' },
  { value: 'DROPDOWN', label: 'Dropdown' },
  { value: 'ENTITY', label: 'Entity' },
  { value: 'CHECKBOX', label: 'Checkbox' },
  { value: 'TEXTAREA', label: 'Textarea' },
  { value: 'BOOLEAN', label: 'Boolean' },
  { value: 'FLOAT', label: 'Float' },
  { value: 'DATE', label: 'Date' },
  { value: 'FILE', label: 'File' },
  { value: 'TIME', label: 'Time' },
  { value: 'CITY', label: 'City' },
  { value: 'PINCODE', label: 'Pincode' },
  { value: 'CUSTOM', label: 'Custom' },
];

export default function Builder() {
  const { draft, setDraft, steps, stepErrors, currentStep } = useWizard();
  const campaigns = draft.campaign_setup.campaigns;
  const [goalTab, setGoalTab] = useState(0);
  const [viewTab, setViewTab] = useState(0);
  const [newField, setNewField] = useState({ alias_name: '', key_type: 'STRING', is_required: false });

  const active = campaigns[goalTab];
  const activeGoal = active?.goal ?? '';
  const fields = active?.form_fields ?? [];

  const updateFields = (goal, next) => {
    setDraft((prev) => ({
      ...prev,
      campaign_setup: {
        ...prev.campaign_setup,
        campaigns: prev.campaign_setup.campaigns.map((c) =>
          c.goal === goal ? { ...c, form_fields: next } : c,
        ),
      },
    }));
  };

  const addField = () => {
    if (!newField.alias_name.trim() || !activeGoal) return;
    const label = newField.alias_name.trim();
    const row = {
      id: crypto.randomUUID?.() ?? String(Date.now()),
      alias_name: label,
      key_name: label.replace(/[^a-zA-Z0-9]+/g, '_').toLowerCase().replace(/(^_|_$)/g, '') || `field_${fields.length}`,
      key_type: newField.key_type,
      is_required: newField.is_required,
    };
    updateFields(activeGoal, [...fields, row]);
    setNewField({ alias_name: '', key_type: 'STRING', is_required: false });
  };

  const removeField = (id) => {
    updateFields(
      activeGoal,
      fields.filter((f) => f.id !== id),
    );
  };

  const formError =
    stepErrors.formFields ||
    Object.entries(stepErrors).find(([k]) => k.startsWith('form_'))?.[1];

  if (campaigns.length === 0) {
    return (
      <>
        <StepPanel stepId={7} stepStatus={steps.form}>
          <Alert severity="warning">Complete campaign objectives in the previous step first.</Alert>
        </StepPanel>
        <WizardFooter stepId={currentStep} />
      </>
    );
  }

  return (
    <>
      <StepPanel stepId={7} stepStatus={steps.form}>
        {formError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {formError}
          </Alert>
        )}
        <Tabs
          value={goalTab}
          onChange={(_, v) => {
            setGoalTab(v);
            setViewTab(0);
          }}
          sx={{ mb: 2 }}
          variant="scrollable"
          scrollButtons="auto"
        >
          {campaigns.map((c) => (
            <Tab
              key={c.goal}
              label={GOAL_LABEL_BY_VALUE[c.goal] ?? c.goal}
            />
          ))}
        </Tabs>
        <Tabs value={viewTab} onChange={(_, v) => setViewTab(v)} sx={{ mb: 2 }}>
          <Tab label="Form Fields" />
          <Tab label="Form Preview" />
        </Tabs>
        {viewTab === 0 && active && (
          <>
            <List dense>
              {fields.map((f) => (
                <ListItem
                  key={f.id}
                  secondaryAction={
                    <IconButton edge="end" onClick={() => removeField(f.id)} size="small">
                      <DeleteIcon />
                    </IconButton>
                  }
                >
                  <DragIndicatorIcon sx={{ mr: 1, color: 'text.disabled' }} />
                  <ListItemText
                    primary={f.alias_name || f.label}
                    secondary={`${f.key_type || f.type}${f.is_required || f.required ? ' · Required' : ''}`}
                  />
                </ListItem>
              ))}
            </List>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2, alignItems: 'flex-start' }}>
              <Field
                label="Field Label"
                value={newField.alias_name}
                onChange={(e) => setNewField({ ...newField, alias_name: e.target.value })}
                sx={{ minWidth: 160 }}
              />
              <Field
                select
                label="Type"
                value={newField.key_type}
                onChange={(e) => setNewField({ ...newField, key_type: e.target.value })}
                sx={{ minWidth: 120 }}
              >
                {AttributeTypes.map((t) => (
                  <MenuItem key={t.value} value={t.value}>
                    {t.label}
                  </MenuItem>
                ))}
              </Field>
              <Button
                variant={newField.is_required ? 'contained' : 'outlined'}
                onClick={() => setNewField({ ...newField, is_required: !newField.is_required })}
              >
                {newField.is_required ? 'Required' : 'Optional'}
              </Button>
              <Button variant="outlined" startIcon={<AddIcon />} onClick={addField}>
                Add Field
              </Button>
            </Box>
          </>
        )}
        {viewTab === 1 && active && (
          <Box sx={{ p: 2, border: '1px dashed', borderColor: 'divider', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              Preview — {GOAL_LABEL_BY_VALUE[active.goal] ?? active.goal}
            </Typography>
            {fields.map((f) => (
              <Field
                key={f.id}
                label={(f.alias_name || f.label) + (f.is_required || f.required ? ' *' : '')}
                placeholder={f.alias_name || f.label}
                disabled
                sx={{ mb: 2 }}
              />
            ))}
          </Box>
        )}
      </StepPanel>
      <WizardFooter stepId={currentStep} />
    </>
  );
}
